'use client';
import { useParams, useRouter } from 'next/navigation';
import React, { useEffect } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import client from '@/clients/customer/api-client';
import { registerOTPSchema } from '@/lib/validations/customer/authSchemas';
import { handleErrors } from '@/lib/helpers/functions';
import { Arrow2 } from '@/components/ui/Icons';
import InputField from '@/components/shared/InputField';
import Button from '@ui/Button';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import TermsAndCondition from '@/components/shared/TermsAndCondition';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import toast from 'react-hot-toast';
import useCustomerAuth from '@/hooks/useCustomerAuth';
import useRestaurantLocale from '@/hooks/useRestaurantLocale';

type FormData = z.infer<ReturnType<typeof registerOTPSchema>>;

interface CreateAccountFormProps {
  userAuthToken?: string;
}

export const CreateAccountForm = ({
  userAuthToken,
}: CreateAccountFormProps) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isMounted, setIsMounted] = React.useState<boolean>(true);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const { checkAuthAndRedirect } = useCustomerAuth();

  const locale = useParams().locale;

  const router = useRouter();
  const t = useTranslations();
  const { restaurantLang } = useRestaurantLocale();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(registerOTPSchema(restaurantLang as 'de' | 'en')),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  // Component mount tracking
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      checkAuthAndRedirect(userAuthToken);
    }
  }, [checkAuthAndRedirect, userAuthToken, isMounted]);

  const onSubmit: SubmitHandler<FormData> = async (data, event) => {
    event?.preventDefault();

    setIsLoading(true);

    try {
      const token = await executeRecaptcha?.('register');

      data['g-recaptcha-response'] = token;

      const res = await client.post('/register', data);

      if (isMounted) setIsLoading(false);
      if (res.status === 200) {
        router.push(`/otp?email=${data.email}`);
      }
    } catch (error: any) {
      if (!isMounted) return;

      if (isMounted) setIsLoading(false);
      handleErrors({ type: 'form', setError, errors: error.response });
      toast.error(error.response?.data?.message ?? 'Unknown error');
    }
  };

  return (
    <form
      className="mb-6 w-full 2xl:mb-[30px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <InputField<FormData>
        name="email"
        type="email"
        label={t('email')}
        placeholder="sample@name.com"
        register={register}
        errors={errors}
        // serverErrors={backendErrors.email}
      />

      <Button
        type="submit"
        gradient
        disabled={isLoading}
        className="group"
        // className="flex h-[58px] w-full items-center justify-center rounded-[40px] bg-gradient-to-r from-[#F5375C] to-[#C33076] align-baseline text-h5 font-semibold text-white 2xl:h-[68px]"
      >
        {isLoading && (
          <span className="mr-[5px]">
            <LoadingSpinner />
          </span>
        )}
        <span className="mr-[14px]">{t('next')}</span>
        {!isLoading && <Arrow2 />}

        {/* <MemberCopy height={21} /> */}
      </Button>
      <TermsAndCondition
        btnText={t('next')}
        lang={locale === 'de-DE' ? 'de' : 'en'}
      />
    </form>
  );
};
